export type ServiceKey =
  | 'branding'
  | 'social'
  | 'ads'
  | 'websites'
  | 'search'
  | 'automation'
  | 'hosting';

export interface ServiceModuleItem {
  number: string;
  title: string;
  description: string;
  deliverables: string[];
}

export interface ServiceRoadmapStep {
  phase: string;
  timeframe: string;
  title: string;
  description: string;
  outcome: string;
}

export interface ServiceComparisonRow {
  dimension: string;
  legacyApproach: string;
  xwebaApproach: string;
}

export interface ServiceFaqItem {
  question: string;
  answer: string;
}

export interface ServiceDetailData {
  key: ServiceKey;
  slug: string;
  path: string;
  index: string;
  badge: string;
  shortTitle: string;
  title: string;
  highlightText: string;
  subtitle: string;
  heroDescription: string;
  whatsappPrefill: string;
  metrics: {
    value: string;
    label: string;
  }[];
  specCardTitle: string;
  specCardSubtitle: string;
  specPillars: {
    title: string;
    detail: string;
  }[];
  modulesHeading: string;
  modulesSubheading: string;
  modules: ServiceModuleItem[];
  roadmapHeading: string;
  roadmapSubheading: string;
  roadmap: ServiceRoadmapStep[];
  comparisonHeading: string;
  comparisonSubheading: string;
  comparisons: ServiceComparisonRow[];
  caseStudySpotlight: {
    client: string;
    url?: string;
    role: string;
    summary: string;
    results: string[];
  };
  faqs: ServiceFaqItem[];
}

export const SERVICE_DETAILS_LIST: ServiceDetailData[] = [
  {
    key: 'branding',
    slug: 'branding-consultant',
    path: '/services/branding-consultant',
    index: '01',
    badge: '20+ YEARS HANDS-ON WORK & DESIGN',
    shortTitle: 'Branding Consultant',
    title: 'Senior Branding Consultant with Over 20 Years of Hands-On Work & Design',
    highlightText: '20+ Years of Hands-On Work & Design',
    subtitle:
      'Two decades of direct creative direction, visual identity craftsmanship, corporate rebranding, and UI/UX design systems.',
    heroDescription:
      'Technology changes every year, but human trust is anchored in unmistakable brand authority. Led by a senior Branding Consultant with over 20 years of hands-on design and creative execution, we architect timeless corporate identities, visual systems, print & packaging collateral, and digital UI/UX languages that position your organization as the undisputed leader in your category.',
    whatsappPrefill:
      "Hello Xweba! I'd like to consult with your Branding Consultant (20+ years hands-on design) regarding our brand identity and visual design.",
    metrics: [
      { value: '20+ Yrs', label: 'Hands-On Design Mastery' },
      { value: '100%', label: 'Bespoke Brand Systems' },
      { value: '3.6x', label: 'Brand Recall & Trust Lift' },
    ],
    specCardTitle: 'BRAND ARCHITECTURE SPECIFICATION',
    specCardSubtitle: '20+ Years Senior Creative Direction',
    specPillars: [
      {
        title: 'Visual Identity & Logo Systems',
        detail:
          'Custom-crafted primary marks, monograms, responsive lockups, custom typography pairings, and psychological color palettes.',
      },
      {
        title: 'Comprehensive Brand Guidelines Book',
        detail:
          'Multi-chapter brand standards manual governing logo usage, grid geometry, tone of voice, photography, and digital/print rules.',
      },
      {
        title: 'UI/UX & Graphic Design Craftsmanship',
        detail:
          'Cohesive visual systems spanning web interfaces, mobile apps, executive pitch decks, packaging, signage, and corporate stationery.',
      },
      {
        title: 'Strategic Positioning & Brand Narrative',
        detail:
          'Clarifying your core value proposition, category differentiation, and messaging hierarchy for high-ticket B2B and consumer buyers.',
      },
    ],
    modulesHeading: '6 Comprehensive Pillars of Our Branding Consultancy',
    modulesSubheading:
      'Every engagement is executed hands-on with over two decades of commercial design experience—never outsourced to junior template designers:',
    modules: [
      {
        number: '01',
        title: 'Brand Discovery, Audit & Market Positioning',
        description:
          'We evaluate your existing brand equity, audit regional and global competitors, and define the strategic positioning whitespace your organization will own.',
        deliverables: [
          'Visual & perceptual audit of all current brand touchpoints',
          'Competitor identity & positioning matrix across 5+ industry peers',
          'Core brand archetype, mission, vision, and value pillar definition',
        ],
      },
      {
        number: '02',
        title: 'Logo Architecture & Visual Mark Craftsmanship',
        description:
          'Precision vector design engineered to scale cleanly from a 16px browser favicon and mobile app icon to large-format architectural signage.',
        deliverables: [
          'Primary wordmark, icon mark, horizontal/vertical lockups & monochrome variants',
          'Optical grid construction and golden-ratio geometric proportioning',
          'Full master vector & raster asset package (SVG, EPS, PDF, PNG, WebP)',
        ],
      },
      {
        number: '03',
        title: 'Typography, Color Psychology & Design Tokens',
        description:
          'We establish a distinctive typographic hierarchy and high-contrast color architecture that meets WCAG AA/AAA accessibility across web and print.',
        deliverables: [
          'Display, editorial, and monospace font licensing & pairing specifications',
          'Pantone, CMYK, RGB, and HEX color systems with semantic UI states',
          'Custom iconography set, illustration style, and photographic art direction',
        ],
      },
      {
        number: '04',
        title: 'Master Brand Guidelines & Governance Playbook',
        description:
          'Your permanent single source of truth so internal teams, printers, and media partners execute your brand with 100% consistency.',
        deliverables: [
          '40+ page interactive PDF & web-hosted Brand Style Guide',
          'Clear Do’s and Don’ts, clear-space rules, and co-branding standards',
          'Brand voice, executive messaging framework, and tagline library',
        ],
      },
      {
        number: '05',
        title: 'Corporate Collateral, Packaging & Environmental Design',
        description:
          'Tangible, high-impact graphic design across every physical and commercial touchpoint your clients experience.',
        deliverables: [
          'Executive business cards, letterheads, company profiles & capability decks',
          'Product packaging, fleet branding, event backdrops, and exhibition booths',
          'Annual reports, investor memorandums, and interactive sales proposals',
        ],
      },
      {
        number: '06',
        title: 'Digital UI/UX Design Systems & Figma Component Libraries',
        description:
          'Bridging classic graphic design rigor with modern interactive web and application interfaces.',
        deliverables: [
          'High-fidelity UI/UX design systems and reusable component tokens',
          'Conversion-centered page layouts, interactive prototypes, and micro-interactions',
          'Direct handoff into Pro WordPress or custom AI web engineering',
        ],
      },
    ],
    roadmapHeading: 'How Our 4-Phase Branding Process Works',
    roadmapSubheading:
      'A structured, collaborative creative process refined over 20+ years of hands-on client work:',
    roadmap: [
      {
        phase: 'PHASE 01',
        timeframe: 'Days 1 – 4',
        title: 'Executive Discovery & Brand Audit',
        description:
          'Deep-dive workshop with leadership to uncover commercial goals, audience psychology, and competitive gaps.',
        outcome: 'Approved Brand Strategy Brief & Positioning Blueprint',
      },
      {
        phase: 'PHASE 02',
        timeframe: 'Days 5 – 10',
        title: 'Visual Direction & Identity Exploration',
        description:
          'Hands-on sketching, vector construction, typographic pairing, and real-world mockup visualization across digital and physical media.',
        outcome: '3 Distinct Senior-Crafted Identity Concepts',
      },
      {
        phase: 'PHASE 03',
        timeframe: 'Days 11 – 16',
        title: 'Refinement, Collateral & UI/UX Systematization',
        description:
          'Polishing the chosen identity direction and extending it across stationery, pitch decks, social templates, and web UI/UX.',
        outcome: 'Complete Brand Collateral & Digital UI Kit',
      },
      {
        phase: 'PHASE 04',
        timeframe: 'Days 17 – 20',
        title: 'Master Brand Book & Asset Handover',
        description:
          'Compiling the comprehensive Brand Guidelines manual and delivering organized production files in all print and web formats.',
        outcome: 'Master Brand Book + 100% IP & Asset Ownership',
      },
    ],
    comparisonHeading: '20+ Years Hands-On Branding vs. Generic Crowd/Template Logos',
    comparisonSubheading:
      'Why established companies and ambitious founders invest in seasoned creative direction:',
    comparisons: [
      {
        dimension: 'Creative Pedigree',
        legacyApproach: 'Junior freelancers or generic AI/Canva clipart templates used by thousands',
        xwebaApproach: 'Over 20 years of hands-on commercial graphic design, UI/UX, and brand strategy',
      },
      {
        dimension: 'Cross-Media Readiness',
        legacyApproach: 'Breaks down when printed on signage, packaging, or scaled to mobile UI',
        xwebaApproach: 'Engineered in CMYK, Pantone, and RGB/SVG for flawless print, fleet, and 4K digital rendering',
      },
      {
        dimension: 'Strategic Alignment',
        legacyApproach: 'Purely decorative graphics disconnected from sales conversion',
        xwebaApproach: 'Rooted in buyer psychology, market differentiation, and conversion architecture',
      },
      {
        dimension: 'Deliverable Completeness',
        legacyApproach: 'A single PNG file with no typography or usage rules',
        xwebaApproach: 'Full Master Brand Book, vector source files, collateral suite, and UI design tokens',
      },
    ],
    caseStudySpotlight: {
      client: 'Golden Wheels Kenya',
      url: 'https://goldenwheels.co.ke/',
      role: 'UI/UX, Graphic Designing, Brand Identity, SEO & GEO',
      summary:
        'Delivered cohesive visual branding, custom graphic design assets, conversion-focused UI/UX design, and full SEO + GEO architecture for Golden Wheels Kenya, elevating brand trust for local and international corporate mobility clients.',
      results: [
        'Complete visual identity alignment & bespoke graphic design suite',
        'High-converting mobile-first UI/UX booking experience',
        'Top-tier Google SEO & AI Search (GEO) brand citation authority',
      ],
    },
    faqs: [
      {
        question: 'What does "over 20 years of hands-on work and design" mean for my project?',
        answer:
          'It means your brand is not delegated to an inexperienced intern or generated from a stock template. Your visual identity, typography, graphic design, and UI/UX are crafted directly with over two decades of real-world print, corporate, and digital design mastery.',
      },
      {
        question: 'Can you modernize our existing brand without losing our current customer recognition?',
        answer:
          'Yes. We specialize in evolutionary brand refreshes—cleaning up legacy geometry, modernizing typography for high-DPI screens, and expanding your color and UI system while preserving the core brand equity your customers already trust.',
      },
      {
        question: 'Do we receive full copyright and source files upon completion?',
        answer:
          '100%. You receive the complete master archive including editable Adobe Illustrator/vector source files (.AI, .EPS, .SVG), print-ready PDFs, high-resolution PNG/WebP assets, font specs, and the Brand Guidelines book.',
      },
      {
        question: 'Can you handle both our corporate branding and our website UI/UX design?',
        answer:
          'Absolutely. Because we combine 20+ years of branding and graphic design with full-stack Pro WordPress and AI web engineering, your brand identity flows seamlessly into your live website, social media, and smart ad campaigns.',
      },
    ],
  },
  {
    key: 'social',
    slug: 'social-media-consulting',
    path: '/services/social-media-consulting',
    index: '02',
    badge: 'STRATEGIC AUDIENCE & COMMUNITY GROWTH',
    shortTitle: 'Social Media Consulting',
    title: 'Social Media Consulting & Executive Digital Community Authority',
    highlightText: 'Social Media Consulting',
    subtitle:
      'Transform scattered social profiles into a cohesive, high-trust brand ecosystem that drives organic demand and inbound sales.',
    heroDescription:
      'Posting random updates without a strategic framework wastes time and dilutes brand authority. Our Social Media Consulting service combines 20+ years of visual design direction with platform-specific growth playbooks across LinkedIn, Instagram, Facebook, X, YouTube, and TikTok—turning passive followers into qualified commercial pipeline.',
    whatsappPrefill:
      "Hello Xweba! I'm interested in your Social Media Consulting service to grow our brand authority and inbound leads.",
    metrics: [
      { value: '3.8x', label: 'Engagement & Reach Lift' },
      { value: '100%', label: 'Brand-Cohesive Visuals' },
      { value: '+240%', label: 'Inbound DM & Profile Clicks' },
    ],
    specCardTitle: 'SOCIAL ECOSYSTEM ARCHITECTURE',
    specCardSubtitle: 'Omnichannel Strategy & Visual Direction',
    specPillars: [
      {
        title: 'Omnichannel Profile & Entity Optimization',
        detail:
          'Bio engineering, visual banners, link-in-bio funnels, and cross-platform entity verification for social search & AI indexing.',
      },
      {
        title: 'Content Pillar & Editorial Calendar Systems',
        detail:
          'Structured monthly content matrices balancing authority education, case study proof, behind-the-scenes trust, and direct offers.',
      },
      {
        title: 'Bespoke Social Graphic & Carousel Design',
        detail:
          'Senior-designed visual templates, LinkedIn/Instagram carousels, and short-form video art direction aligned with your brand book.',
      },
      {
        title: 'Social Selling & DM Conversion Playbooks',
        detail:
          'Workflows that route high-intent comments, profile visits, and direct messages into WhatsApp and booked strategy calls.',
      },
    ],
    modulesHeading: '6 Core Modules of Our Social Media Consulting',
    modulesSubheading:
      'We build repeatable social growth systems whether we manage your channels directly or train and equip your internal marketing team:',
    modules: [
      {
        number: '01',
        title: 'Social Audit, Competitor Benchmarking & Channel Strategy',
        description:
          'We analyze your current social presence, audience demographics, and competitor content performance to identify high-leverage platforms.',
        deliverables: [
          'Deep-dive audit of LinkedIn, Instagram, Facebook, X, and YouTube profiles',
          'Audience persona mapping & platform prioritization matrix',
          '90-day strategic growth roadmap with clear KPIs',
        ],
      },
      {
        number: '02',
        title: 'Profile Makeovers & Social SEO Optimization',
        description:
          'Modern users use Instagram, TikTok, and LinkedIn like search engines. We optimize every profile element for keyword discoverability and instant conversion.',
        deliverables: [
          'Custom-designed cover banners, profile avatars, and highlight covers',
          'Keyword-optimized headlines, bios, and service showcases',
          'High-converting funnel links routed to your website and WhatsApp',
        ],
      },
      {
        number: '03',
        title: 'Content Pillars, Scripting & Editorial Playbooks',
        description:
          'Never stare at a blank calendar again. We establish 4 to 5 core content pillars that systematically educate buyers and overcome objections.',
        deliverables: [
          '30-day rolling editorial calendar with hooks, body copy, and CTAs',
          'Short-form video & Reel scripts (hooks, storyboards, and retention cues)',
          'Executive thought-leadership ghostwriting for founders & directors',
        ],
      },
      {
        number: '04',
        title: 'Senior Graphic Design & Visual Template Systems',
        description:
          'Backed by 20+ years of hands-on graphic design, we create thumb-stopping social visuals that make your brand unmistakably recognizable in the feed.',
        deliverables: [
          'Bespoke multi-slide educational carousel systems for LinkedIn & Instagram',
          'Branded quote cards, case study graphics, and infographic templates',
          'Editable design kits so your team can produce on-brand graphics in minutes',
        ],
      },
      {
        number: '05',
        title: 'Community Management & Social Selling Workflows',
        description:
          'Likes do not pay bills—conversations do. We design ethical, high-converting social selling frameworks that move prospects from comments to calls.',
        deliverables: [
          'Comment-to-DM lead magnet & resource delivery playbooks',
          'Inbound DM qualification scripts and WhatsApp handoff protocols',
          'Reputation management and crisis response guidelines',
        ],
      },
      {
        number: '06',
        title: 'Analytics, Attribution & In-House Team Coaching',
        description:
          'We track what actually drives website traffic, leads, and revenue—and upskill your internal staff to sustain momentum.',
        deliverables: [
          'Monthly executive performance & attribution review',
          'Content retention, share-rate, and conversion velocity analysis',
          '1-on-1 or group training workshops for internal social media coordinators',
        ],
      },
    ],
    roadmapHeading: '4-Step Social Media Transformation Roadmap',
    roadmapSubheading:
      'From fragmented posting to an authoritative, revenue-generating social presence:',
    roadmap: [
      {
        phase: 'PHASE 01',
        timeframe: 'Week 1',
        title: 'Channel Audit & Positioning Alignment',
        description:
          'Evaluating existing social channels, competitor content, and aligning social tone with your commercial objectives.',
        outcome: 'Omnichannel Social Strategy & Audit Report',
      },
      {
        phase: 'PHASE 02',
        timeframe: 'Week 2',
        title: 'Profile Revamp & Visual System Creation',
        description:
          'Overhauling bios, banners, highlights, and designing your custom social graphic & carousel system.',
        outcome: 'Optimized Profiles & Branded Visual Kit',
      },
      {
        phase: 'PHASE 03',
        timeframe: 'Weeks 3 – 4',
        title: 'Editorial Launch & Social Selling Activation',
        description:
          'Rolling out high-authority content pillars, executive posts, and DM conversion workflows.',
        outcome: 'Active 30-Day Content Engine & Lead Funnel',
      },
      {
        phase: 'PHASE 04',
        timeframe: 'Ongoing Monthly',
        title: 'Analytics Optimization & Scale',
        description:
          'Doubling down on top-performing formats, amplifying winning posts with Smart Ads, and coaching your team.',
        outcome: 'Compounding Organic Reach & Inbound Pipeline',
      },
    ],
    comparisonHeading: 'Strategic Social Media Consulting vs. Generic Posting',
    comparisonSubheading:
      'See how structured social consulting outperforms generic "3 posts a week" packages:',
    comparisons: [
      {
        dimension: 'Content Purpose',
        legacyApproach: 'Generic holiday graphics and filler posts with zero buyer intent',
        xwebaApproach: 'Authority-building carousels, case studies, and buyer-objection playbooks',
      },
      {
        dimension: 'Visual Quality',
        legacyApproach: 'Inconsistent, off-brand templates that look cheap',
        xwebaApproach: 'Crafted with 20+ years of senior graphic design and brand identity mastery',
      },
      {
        dimension: 'Lead Conversion',
        legacyApproach: 'Chasing vanity likes with no path to a sales conversation',
        xwebaApproach: 'Structured comment-to-DM, WhatsApp, and landing page conversion funnels',
      },
      {
        dimension: 'AI & Search Synergy',
        legacyApproach: 'Siloed social posts invisible to search engines',
        xwebaApproach: 'Social entity signals cross-linked with your website Schema.org graph',
      },
    ],
    caseStudySpotlight: {
      client: 'Enterprise & B2B Executive Advisory',
      role: 'LinkedIn & Instagram Brand Authority System',
      summary:
        'Transformed dormant corporate social channels into a cohesive authority engine using custom graphic carousel frameworks, executive positioning, and direct WhatsApp booking funnels.',
      results: [
        '3.8x increase in qualified profile engagement within 60 days',
        'Consistent visual brand identity across LinkedIn, Instagram & Facebook',
        'Direct attribution of high-ticket consultations from social content',
      ],
    },
    faqs: [
      {
        question: 'Do you provide consulting for our in-house team, or can you also design and manage the content?',
        answer:
          'We offer both. We can serve as your strategic Social Media Consultant—providing the roadmap, visual templates, content calendar, and coaching for your team—or handle full turnkey graphic design, copywriting, and publishing for you.',
      },
      {
        question: 'Which social media platforms do you specialize in?',
        answer:
          'We specialize in LinkedIn (for B2B, corporate, and executive authority), Instagram and Facebook (for visual storytelling, retail, hospitality, and services), X (Twitter), and YouTube/TikTok short-form video strategy.',
      },
      {
        question: 'How does Social Media Consulting connect with SEO and GEO?',
        answer:
          'AI search engines (ChatGPT, Perplexity, Google) validate your brand’s real-world authority by checking consistent entity signals across your official social profiles (`sameAs` schema links) and website content.',
      },
      {
        question: 'How quickly will we see improvements in our social presence?',
        answer:
          'Your profile revamp and visual identity upgrade are completed within the first 10–14 days, immediately elevating how prospects perceive your brand. Inbound engagement and lead flow typically compound across days 30 to 90.',
      },
    ],
  },
  {
    key: 'ads',
    slug: 'digital-smart-advertising',
    path: '/services/digital-smart-advertising',
    index: '03',
    badge: 'HIGH-ROAS PERFORMANCE MEDIA',
    shortTitle: 'Digital / Smart Advertisement',
    title: 'Digital / Smart Advertisement & Precision Performance Media',
    highlightText: 'Digital / Smart Advertisement',
    subtitle:
      'Data-driven paid media combining 20+ years of persuasive creative design with algorithmic ad targeting across Google, Meta & LinkedIn.',
    heroDescription:
      'Clicking "Boost Post" without conversion architecture is the fastest way to burn marketing budget. Our Digital / Smart Advertisement service pairs senior-crafted ad creatives and direct-response copy with server-side pixel tracking, algorithmic bidding, and dedicated high-speed landing pages—turning ad spend into predictable, measurable revenue.',
    whatsappPrefill:
      "Hello Xweba! I'm interested in launching Digital / Smart Advertisement campaigns for high-ROI lead generation.",
    metrics: [
      { value: '4.5x', label: 'Target Campaign ROAS' },
      { value: '-42%', label: 'Lower Cost Per Lead (CPL)' },
      { value: '100%', label: 'Full-Funnel Pixel Tracking' },
    ],
    specCardTitle: 'SMART AD ENGINE SPECIFICATION',
    specCardSubtitle: 'Multi-Channel Acquisition & Attribution',
    specPillars: [
      {
        title: 'Google Search, Performance Max & YouTube Ads',
        detail:
          'Capturing high-intent buyers at the exact moment they search for your services or research competitors on Google.',
      },
      {
        title: 'Meta (Instagram & Facebook) & LinkedIn Smart Ads',
        detail:
          'Algorithmic audience segmentation, lookalike modeling, and B2B decision-maker targeting by job title and industry.',
      },
      {
        title: '20+ Years Persuasive Ad Creative & Graphic Design',
        detail:
          'Scroll-stopping static graphics, carousel ads, motion visuals, and A/B tested direct-response headlines.',
      },
      {
        title: 'Conversion API, Pixel & WhatsApp Lead Routing',
        detail:
          'Google Tag Manager, Meta Pixel, and instant click-to-WhatsApp / CRM lead capture so every click is tracked to revenue.',
      },
    ],
    modulesHeading: '6 Pillars of Our Digital / Smart Advertisement Architecture',
    modulesSubheading:
      'We engineer every stage of the paid acquisition funnel—from impression to click, landing page, and closed sale:',
    modules: [
      {
        number: '01',
        title: 'Paid Media Audit, Offer Engineering & Funnel Strategy',
        description:
          'Before spending a single dollar, we audit past ad accounts, analyze unit economics, and structure an irresistible front-end offer.',
        deliverables: [
          'Historical ad account waste & leakage diagnostic',
          'Target CPA (Cost Per Acquisition) and ROAS financial modeling',
          'Channel mix allocation across Google, Meta, LinkedIn, and YouTube',
        ],
      },
      {
        number: '02',
        title: 'High-Converting Ad Creative & Direct-Response Copy',
        description:
          'In 2026, creative is the #1 targeting lever. We leverage 20+ years of graphic design mastery to produce high-CTR visual ads.',
        deliverables: [
          'Bespoke ad graphic suites (1:1 feed, 9:16 story/reel, 16:9 display)',
          'Multi-variant direct-response ad copy (PAS & value-first frameworks)',
          'Continuous creative testing to eliminate ad fatigue',
        ],
      },
      {
        number: '03',
        title: 'Smart Audience Segmentation & Algorithmic Bidding',
        description:
          'We combine first-party CRM data with platform AI bidding signals to reach buyers who are ready to purchase.',
        deliverables: [
          'High-intent exact & phrase match Google Search keyword architecture',
          'Negative keywordlists to block irrelevant clicks and budget drain',
          'Custom CRM customer list uploads & 1%–3% Lookalike audiences',
        ],
      },
      {
        number: '04',
        title: 'Tracking Pixels, Conversion API & Attribution Setup',
        description:
          'Ad algorithms only optimize when fed accurate conversion signals. We instrument bulletproof tracking across your web stack.',
        deliverables: [
          'Google Tag Manager (GTM), GA4, and Google Ads conversion linker',
          'Meta Pixel & Server-Side Conversion API (CAPI) event configuration',
          'Click-to-WhatsApp, phone call, and form submission event tracking',
        ],
      },
      {
        number: '05',
        title: 'Message-Matched Conversion Landing Pages',
        description:
          'Sending paid traffic to a slow, generic homepage kills conversion rates. We build dedicated, sub-second ad landing pages.',
        deliverables: [
          'Headline and offer message-matching for higher Google Quality Scores',
          'Sub-second mobile load speed (<0.8s) to eliminate click drop-off',
          'Instant WhatsApp & AI Concierge lead capture embedded on the page',
        ],
      },
      {
        number: '06',
        title: ' Omnichannel Retargeting & Weekly ROAS Optimization',
        description:
          'Up to 90% of buyers do not convert on their first visit. Our smart retargeting loops bring warm visitors back with social proof.',
        deliverables: [
          'Sequential 7-day, 14-day, and 30-day retargeting campaigns',
          'Weekly bid, placement, and cost-per-lead optimization sprints',
          'Transparent executive reporting on ad spend, leads, and pipeline ROI',
        ],
      },
    ],
    roadmapHeading: '4-Phase Smart Advertising Launch Protocol',
    roadmapSubheading:
      'How we launch, test, and scale profitable Digital / Smart Advertisement campaigns:',
    roadmap: [
      {
        phase: 'PHASE 01',
        timeframe: 'Days 1 – 3',
        title: 'Offer Strategy, Pixel Instrumentation & Tracking',
        description:
          'Configuring Google Tag Manager, Meta Pixel, conversion events, and defining target CPA/ROAS benchmarks.',
        outcome: 'Verified Tracking & Campaign Blueprint',
      },
      {
        phase: 'PHASE 02',
        timeframe: 'Days 4 – 7',
        title: 'Creative Production & Landing Page Alignment',
        description:
          'Designing high-impact ad graphics, writing direct-response copy, and optimizing the destination landing page.',
        outcome: 'Approved Ad Creatives & High-Speed Landing Page',
      },
      {
        phase: 'PHASE 03',
        timeframe: 'Days 8 – 21',
        title: 'Campaign Launch & Algorithmic Split-Testing',
        description:
          'Launching structured ad sets across Google/Meta/LinkedIn and testing hooks, visuals, and audience segments.',
        outcome: 'Validated Winning Ad Combinations & Inbound Leads',
      },
      {
        phase: 'PHASE 04',
        timeframe: 'Day 22+ Ongoing',
        title: 'Budget Scaling & Retargeting Loops',
        description:
          'Pruning underperforming placements, scaling budget into high-ROAS winners, and activating full-funnel retargeting.',
        outcome: 'Predictable, Scalable Customer Acquisition',
      },
    ],
    comparisonHeading: 'Xweba Smart Advertising vs. Unoptimized Ad Boosting',
    comparisonSubheading:
      'Why disciplined ad engineering + 20+ years of design craft delivers superior return on ad spend:',
    comparisons: [
      {
        dimension: 'Campaign Setup',
        legacyApproach: 'Clicking "Boost Post" with broad, unqualified targeting',
        xwebaApproach: 'Full Ads Manager architecture with conversion objectives, negative keywords & lookalikes',
      },
      {
        dimension: 'Ad Creative',
        legacyApproach: 'Cluttered flyers that get ignored in the scroll',
        xwebaApproach: 'Psychology-backed visual creatives designed with 20+ years of commercial art direction',
      },
      {
        dimension: 'Post-Click Experience',
        legacyApproach: 'Slow 5-second homepage with no clear call to action',
        xwebaApproach: 'Sub-second message-matched landing page with instant WhatsApp & CRM routing',
      },
      {
        dimension: 'Measurement',
        legacyApproach: 'Reporting only "impressions" and "clicks" with zero revenue visibility',
        xwebaApproach: 'End-to-end cost-per-qualified-lead (CPQL) and closed pipeline attribution',
      },
    ],
    caseStudySpotlight: {
      client: 'Multi-Industry Growth Campaigns (East Africa & Global)',
      role: 'Google Search, Meta Retargeting & Conversion Landing Pages',
      summary:
        'Combined high-intent Google Search capture with Meta visual retargeting and instant WhatsApp routing, cutting cost-per-lead by 42% while achieving a 4.5x return on ad spend.',
      results: [
        '4.5x average Return on Ad Spend (ROAS) across active campaigns',
        '42% reduction in Cost Per Qualified Lead via landing page speed & UX',
        'Sub-60-second lead follow-up via automated WhatsApp & CRM dispatch',
      ],
    },
    faqs: [
      {
        question: 'Which ad platforms are included in Digital / Smart Advertisement?',
        answer:
          'We design and manage campaigns across Google Ads (Search, Performance Max, Display, YouTube), Meta Ads (Instagram, Facebook, and Click-to-WhatsApp ads), and LinkedIn Ads for B2B executive targeting.',
      },
      {
        question: 'Do you design all the ad graphics and write the ad copy?',
        answer:
          'Yes. Backed by over 20 years of hands-on graphic design and branding experience, we design all static and carousel ad visuals, write the direct-response ad copy, and build or optimize your landing pages.',
      },
      {
        question: 'Who pays the ad networks (Google/Meta)?',
        answer:
          'You retain 100% ownership of your ad accounts and pay Google or Meta directly with your company card for 100% financial transparency, while Xweba manages the strategy, creative design, targeting, and daily optimization.',
      },
      {
        question: 'Can Smart Ads route leads directly to our WhatsApp and CRM?',
        answer:
          'Yes! We configure both Click-to-WhatsApp ad flows and high-speed landing page forms that push qualified leads straight to your WhatsApp (+254 725 724 175) and CRM pipeline in real time.',
      },
    ],
  },
  {
    key: 'websites',
    slug: 'pro-wordpress-ai-websites',
    path: '/ai-websites',
    index: '04',
    badge: 'PRO WORDPRESS & AI-CITED WEB ENGINEERING',
    shortTitle: 'Pro WordPress & AI Websites',
    title: 'Design Pro WordPress Websites That Are Cited by AI & Custom Web Platforms',
    highlightText: 'Cited by AI',
    subtitle:
      'Enterprise-grade Pro WordPress websites and custom web applications engineered for sub-second speed, human persuasion, and direct AI search citation.',
    heroDescription:
      'Most WordPress websites are weighed down by bloated multipurpose themes and are completely invisible to AI answer engines. At Xweba, we design custom Pro WordPress Websites that are cited by AI—combining 20+ years of UI/UX design mastery, LiteSpeed Hostinger Cloud caching, Schema.org entity graphs, and /llms.txt architecture so ChatGPT, Perplexity, and Google cite and recommend your business.',
    whatsappPrefill:
      "Hello Xweba! I'm interested in designing a Pro WordPress Website that is cited by AI.",
    metrics: [
      { value: '<0.8s', label: 'Core Web Vitals Speed' },
      { value: '99/100', label: 'Google PageSpeed Score' },
      { value: '+310%', label: 'AI Search Citation Lift' },
    ],
    specCardTitle: 'PRO WORDPRESS & AI WEB SPECIFICATION',
    specCardSubtitle: '2026 AI-Citation & Conversion Standard',
    specPillars: [
      {
        title: 'Bespoke Pro WordPress & WooCommerce Engineering',
        detail:
          'Clean, lightweight custom WordPress theme architecture and WooCommerce stores with zero plugin bloat and effortless CMS editing.',
      },
      {
        title: 'Engineered to Be Cited by AI (GEO Native)',
        detail:
          'Embedded JSON-LD Schema.org graphs, /llms.txt markdown grounding, and structured answer blocks for ChatGPT & Perplexity.',
      },
      {
        title: 'Custom React, Next.js & Headless Web Platforms',
        detail:
          'For complex SaaS, portals, and interactive web apps, we engineer custom TypeScript/React platforms or headless WordPress stacks.',
      },
      {
        title: 'Hostinger Cloud LiteSpeed & Mobile Ergonomics',
        detail:
          'Sub-second mobile 4G load speeds, NVMe cloud storage, global CDN, and thumb-friendly conversion UX.',
      },
    ],
    modulesHeading: '6 Engineering Deliverables Inside Every Pro WordPress & AI Website',
    modulesSubheading:
      'Every website we build is a high-performance commercial asset designed for both human buyers and AI crawlers:',
    modules: [
      {
        number: '01',
        title: 'Pro WordPress Architecture Engineered for AI Citations',
        description:
          'We build clean, custom Pro WordPress websites stripped of legacy script bloat and enriched with machine-readable entity data so LLMs cite your pages.',
        deliverables: [
          'Custom-designed Pro WordPress theme tailored to your brand identity',
          'Automated JSON-LD Schema.org injection (Organization, Service, FAQPage, Product)',
          'Standardized /llms.txt and /llms-full.txt AI crawler discovery files',
        ],
      },
      {
        number: '02',
        title: '20+ Years Hands-On UI/UX & Visual Brand Craft',
        description:
          'Every layout, typographic scale, and visual interaction is crafted by a senior Branding Consultant with over 20 years of hands-on design experience.',
        deliverables: [
          'Bespoke desktop, tablet, and mobile-first responsive UI/UX design',
          'High-contrast editorial typography and zero-clutter visual hierarchy',
          'Custom iconography, brand graphics, and conversion-focused page layouts',
        ],
      },
      {
        number: '03',
        title: 'WooCommerce, Payment Gateways & Booking Systems',
        description:
          'Whether you sell products, safari tours, corporate services, or consultations, we integrate frictionless checkout and booking flows.',
        deliverables: [
          'M-Pesa (Daraja API), Visa/Mastercard, Stripe, Pesapal & Flutterwave integration',
          'High-converting WooCommerce product catalogs and 1-page checkout flows',
          'Interactive quote calculators and automated calendar appointment booking',
        ],
      },
      {
        number: '04',
        title: 'AI-Assisted Persuasive Copywriting & Citation Hubs',
        description:
          'We write clear, authoritative website copy structured with direct answer nodes that persuade executives and get quoted by AI search engines.',
        deliverables: [
          'Conversion-engineered headlines, service breakdowns, and case study narratives',
          'RAG-ready FAQ sections formatted for Google Snippets & AI Overviews',
          'Internal topic-cluster linking architecture for deep domain authority',
        ],
      },
      {
        number: '05',
        title: 'Sub-Second Speed & Security Hardening',
        description:
          'Speed is a direct ranking and conversion factor. We optimize every asset for instant rendering on mobile and desktop networks.',
        deliverables: [
          'LiteSpeed Enterprise Object Caching, Redis & Cloudflare CDN optimization',
          'Next-gen WebP/AVIF image compression and critical CSS inlining',
          'WAF firewall protection, SSL/TLS 1.3, HSTS headers, and daily cloud backups',
        ],
      },
      {
        number: '06',
        title: 'One-Time Build or Website-as-a-Service (WaaS)',
        description:
          'Choose a traditional one-off build or our popular WaaS subscription where we manage hosting, security, content updates, and GEO growth monthly.',
        deliverables: [
          'Intuitive WordPress editor training so your team can publish in minutes',
          '100% client ownership of domain, WordPress admin, and codebase',
          'Optional monthly WaaS care plan with continuous AI search optimization',
        ],
      },
    ],
    roadmapHeading: '10-Day Sprint Delivery for Pro WordPress & AI Websites',
    roadmapSubheading:
      'No 6-month agency delays. Our disciplined sprint methodology takes you from kickoff to live launch rapidly:',
    roadmap: [
      {
        phase: 'PHASE 01',
        timeframe: 'Days 1 – 2',
        title: 'Architecture, Sitemap & Entity Planning',
        description:
          'Mapping your site structure, conversion paths, target keywords, and AI entity schema graph.',
        outcome: 'Approved Sitemap & Conversion Wireframe',
      },
      {
        phase: 'PHASE 02',
        timeframe: 'Days 3 – 5',
        title: 'Bespoke UI/UX Design & Copywriting',
        description:
          'Crafting your visual interface with 20+ years of design mastery alongside citation-ready copy.',
        outcome: 'High-Fidelity Responsive UI & Copy Deck',
      },
      {
        phase: 'PHASE 03',
        timeframe: 'Days 6 – 8',
        title: 'Pro WordPress / Full-Stack Build & GEO Injection',
        description:
          'Developing the site, integrating WooCommerce/booking/WhatsApp flows, and embedding JSON-LD + /llms.txt.',
        outcome: 'Staging Website Scoring 95+ on PageSpeed & Xwebaudit',
      },
      {
        phase: 'PHASE 04',
        timeframe: 'Days 9 – 10',
        title: 'Hostinger Cloud Launch, Indexing & Handover',
        description:
          'Deploying to production SSL, submitting sitemaps to Google & AI crawlers, and training your team.',
        outcome: 'Live AI-Cited Website + Full Admin Access',
      },
    ],
    comparisonHeading: 'Why Ordinary WordPress Sites Fail vs. Xweba Pro WordPress Cited by AI',
    comparisonSubheading:
      'Compare standard template builds against our AI-cited Pro WordPress engineering:',
    comparisons: [
      {
        dimension: 'AI Search Visibility',
        legacyApproach: 'Unstructured HTML & plugin soup ignored by ChatGPT and Perplexity',
        xwebaApproach: 'Built-in Schema.org entity graphs, /llms.txt, and semantic answer hubs cited by AI',
      },
      {
        dimension: 'Page Load Speed',
        legacyApproach: '4.0s – 7.5s load time due to heavy page builders and 40+ slow plugins',
        xwebaApproach: '<0.8s load time on Hostinger Cloud LiteSpeed with clean custom architecture',
      },
      {
        dimension: 'Visual UI/UX Design',
        legacyApproach: 'Cookie-cutter $59 theme that looks identical to competitors',
        xwebaApproach: 'Bespoke visual identity crafted with 20+ years of hands-on design expertise',
      },
      {
        dimension: 'Lead Capture',
        legacyApproach: 'Basic email form that goes to spam or waits days for a reply',
        xwebaApproach: '24/7 AI Concierge, instant WhatsApp routing, and automated CRM sync',
      },
    ],
    caseStudySpotlight: {
      client: 'Golden Wheels Kenya',
      url: 'https://goldenwheels.co.ke/',
      role: 'UI/UX, Graphic Designing, SEO & GEO Web Platform',
      summary:
        'Engineered a high-speed, mobile-first web experience for Golden Wheels Kenya combining bespoke UI/UX, custom graphic design, and structured SEO + GEO entity architecture for maximum organic and AI search visibility.',
      results: [
        'Sub-second mobile load performance across Kenyan & international networks',
        'Structured Schema.org vehicle & service entity graph for AI citations',
        'Seamless mobile booking & WhatsApp reservation workflow',
      ],
    },
    faqs: [
      {
        question: 'How do you make a WordPress website get cited by AI (ChatGPT, Perplexity, Google AI Overviews)?',
        answer:
          'AI engines do not read websites the way humans do—they parse semantic HTML5, JSON-LD Schema.org entity graphs, and standardized /llms.txt files, and extract concise factual answer blocks. We engineer all of these directly into your Pro WordPress architecture so AI models trust and cite your domain as the primary source.',
      },
      {
        question: 'Will our team be able to edit text, images, and blog posts easily in WordPress?',
        answer:
          'Yes! That is why clients love our Pro WordPress builds. You get a clean, intuitive WordPress backend where non-technical staff can update pages, publish articles, or manage WooCommerce products in minutes—while the underlying code remains ultra-fast and AI-optimized.',
      },
      {
        question: 'Can you redesign and migrate our existing slow WordPress website without losing our Google rankings?',
        answer:
          'Yes. We perform a full URL audit, preserve or 301-redirect all existing indexed URLs, migrate your content cleanly, and upgrade your technical SEO and GEO schemas so your rankings and AI visibility surge after launch.',
      },
      {
        question: 'Do you also build custom React/TypeScript web applications?',
        answer:
          'Yes. Whenever a project requires custom SaaS functionality, interactive client portals, or headless speed, we engineer full-stack React, TypeScript, and Node.js applications alongside or integrated with Pro WordPress.',
      },
    ],
  },
  {
    key: 'search',
    slug: 'geo-optimization',
    path: '/ai-search-visibility',
    index: '05',
    badge: 'SEO + GENERATIVE ENGINE OPTIMIZATION (GEO)',
    shortTitle: 'AI Search Visibility (GEO)',
    title: 'Generative Engine Optimization (GEO) & Google SEO Authority',
    highlightText: 'ChatGPT, Perplexity & Google',
    subtitle:
      'Get cited, verified, and recommended when high-intent buyers search on Google or prompt ChatGPT, Perplexity, Claude, and Gemini.',
    heroDescription:
      'Over 40% of commercial research queries now trigger AI-synthesized answers rather than traditional 10 blue links. Our dual SEO + Generative Engine Optimization (GEO) service transforms your website into a machine-readable authority graph—ensuring your business ranks at the top of Google Search while being directly quoted by AI answer engines.',
    whatsappPrefill:
      "Hello Xweba! I'd like to improve our SEO and Generative Engine Optimization (GEO) so ChatGPT and Google cite our business.",
    metrics: [
      { value: '3.4x', label: 'More AI Engine Citations' },
      { value: '100/100', label: 'Schema & GEO Compliance' },
      { value: '+185%', label: 'Organic High-Intent Traffic' },
    ],
    specCardTitle: 'DUAL SEO + GEO ENGINE SPECIFICATION',
    specCardSubtitle: '2026 Entity & Citation Architecture',
    specPillars: [
      {
        title: 'Multi-Entity JSON-LD Schema.org Graph',
        detail:
          'Interlinked Organization, ProfessionalService, LocalBusiness, Product, FAQPage, and BreadcrumbList schemas.',
      },
      {
        title: '/llms.txt & /llms-full.txt Grounding Protocol',
        detail:
          'Dedicated markdown context endpoints engineered for GPTBot, ClaudeBot, PerplexityBot, and Google-Extended.',
      },
      {
        title: 'RAG-Ready Answer Nodes & Topic Clusters',
        detail:
          'High-density factual summaries, comparison tables, and Q&A hubs structured for zero-hallucination LLM extraction.',
      },
      {
        title: 'Technical Google SEO & E-E-A-T Authority',
        detail:
          'Core Web Vitals speed, XML/News sitemaps, canonical hygiene, author credentials, and external citation alignment.',
      },
    ],
    modulesHeading: '6 Technical Pillars of Our SEO & GEO Implementation',
    modulesSubheading:
      'We bridge classic search engine optimization with 2026 Generative Engine Optimization so you dominate every search surface:',
    modules: [
      {
        number: '01',
        title: '25-Point Xwebaudit & Competitor Citation Gap Analysis',
        description:
          'We test how ChatGPT, Perplexity, Gemini, and Google currently perceive your brand—and identify why competitors are being cited instead.',
        deliverables: [
          'Full technical SEO, Schema.org, and AI crawler accessibility diagnostic',
          'Prompt simulation across 20+ high-intent buyer queries in your industry',
          'Prioritized 90-day SEO + GEO implementation roadmap',
        ],
      },
      {
        number: '02',
        title: 'Complete Schema.org JSON-LD Entity Graph Deployment',
        description:
          'LLMs rely on structured data to verify facts. We code custom, validation-tested JSON-LD graphs across every page of your website.',
        deliverables: [
          'Organization, LocalBusiness / ProfessionalService & Founder Person schemas',
          'Service, OfferCatalog, Product, and AggregateRating structured data',
          'FAQPage, Article, and BreadcrumbList schema with `sameAs` authority links',
        ],
      },
      {
        number: '03',
        title: 'Standardized /llms.txt & AI Crawler Governance',
        description:
          'Give AI crawlers a clean, authoritative cheat-sheet of your services, pricing tiers, locations, and official contact endpoints.',
        deliverables: [
          'Custom-authored /llms.txt and /llms-full.txt markdown documentation',
          'Robots.txt directives explicitly permitting GPTBot, PerplexityBot & ClaudeBot',
          'Machine-discoverable /.well-known/ai-catalog.json and OpenAPI/MCP cards',
        ],
      },
      {
        number: '04',
        title: 'Citation-Optimized Content & Topic Cluster Engineering',
        description:
          'We restructure your service pages and Digital Pulse blog into interlinked topic clusters containing direct, quotable answer blocks.',
        deliverables: [
          '40–60 word executive definition blocks at the top of key pages for snippet extraction',
          'Structured comparison tables and benchmark statistics that LLMs love to cite',
          'Interlinked pillar-and-cluster blog articles indexed for Google News & Search',
        ],
      },
      {
        number: '05',
        title: 'Local SEO, Google Business Profile & Knowledge Graph Signals',
        description:
          'For regional and multi-location brands, we align your website entity data with Google Maps, directories, and third-party review platforms.',
        deliverables: [
          'Google Business Profile (GBP) optimization and category alignment',
          'NAP (Name, Address, Phone) consistency across tier-1 industry directories',
          'Verified Trustindex / Google review schema integration for social proof',
        ],
      },
      {
        number: '06',
        title: 'Continuous AI Citation Monitoring & Monthly Reporting',
        description:
          'Search algorithms evolve monthly. We track your Google keyword rankings alongside your brand mention frequency inside AI engines.',
        deliverables: [
          'Monthly ranking & AI share-of-voice tracking across core commercial prompts',
          'Quarterly schema and /llms.txt updates as you launch new offerings',
          'Ongoing authority content publishing via Digital Pulse',
        ],
      },
    ],
    roadmapHeading: '4-Phase SEO & GEO Deployment Roadmap',
    roadmapSubheading:
      'How we systematically elevate your domain from invisible to the #1 cited authority:',
    roadmap: [
      {
        phase: 'PHASE 01',
        timeframe: 'Week 1',
        title: 'Technical Audit & Entity Mapping',
        description:
          'Scanning your domain with Xwebaudit, auditing crawlability, and mapping your core business entities.',
        outcome: 'Baseline GEO Score & Entity Graph Blueprint',
      },
      {
        phase: 'PHASE 02',
        timeframe: 'Weeks 2 – 3',
        title: 'Schema.org, /llms.txt & On-Page Overhaul',
        description:
          'Deploying JSON-LD schemas, publishing /llms.txt, fixing meta/canonical tags, and restructuring page headings.',
        outcome: '100% Technical SEO & GEO Compliance',
      },
      {
        phase: 'PHASE 03',
        timeframe: 'Weeks 4 – 8',
        title: 'Citation Hubs & Topic Cluster Publishing',
        description:
          'Publishing RAG-optimized FAQ hubs, comparison guides, and interlinked authority articles.',
        outcome: 'Indexed Topic Clusters & Featured Snippets',
      },
      {
        phase: 'PHASE 04',
        timeframe: 'Ongoing Monthly',
        title: 'Authority Scaling & Citation Defense',
        description:
          'Monitoring AI prompt responses, expanding external citations, and compounding organic search traffic.',
        outcome: 'Sustained #1 AI & Google Search Visibility',
      },
    ],
    comparisonHeading: 'Legacy 2018 SEO vs. Xweba 2026 Dual SEO + GEO',
    comparisonSubheading:
      'Why traditional keyword stuffing is no longer enough in the age of AI search:',
    comparisons: [
      {
        dimension: 'Target Engines',
        legacyApproach: 'Only optimizes for traditional Google 10-blue-link web results',
        xwebaApproach: 'Optimizes for Google Search, Google AI Overviews, ChatGPT, Perplexity & Claude',
      },
      {
        dimension: 'Content Structure',
        legacyApproach: 'Long, fluffy 2,000-word blog posts stuffed with repetitive keywords',
        xwebaApproach: 'High-density factual answer nodes, comparison tables, and verified E-E-A-T citations',
      },
      {
        dimension: 'Machine Readability',
        legacyApproach: 'Basic meta tags with zero or broken JSON-LD schema',
        xwebaApproach: 'Multi-entity Schema.org @graph + /llms.txt + AI crawler permission rules',
      },
      {
        dimension: 'Commercial Traffic Quality',
        legacyApproach: 'High-bounce informational clicks that rarely convert',
        xwebaApproach: 'Pre-sold, high-intent buyers referred directly by AI recommendations',
      },
    ],
    caseStudySpotlight: {
      client: 'Golden Wheels Kenya & Xweba Platform Benchmark',
      url: 'https://goldenwheels.co.ke/',
      role: 'Full Technical SEO + Generative Engine Optimization (GEO)',
      summary:
        'Implemented multi-entity JSON-LD Schema.org graphs, semantic HTML5 structure, and localized + international search optimization for Golden Wheels Kenya, achieving top organic discoverability and AI citation readiness.',
      results: [
        '97/100 Xwebaudit GEO & Entity Schema benchmark score',
        'Direct citation extraction across conversational travel & mobility prompts',
        '3.4x higher conversion rate from AI-referred visitors vs. cold traffic',
      ],
    },
    faqs: [
      {
        question: 'What is the difference between SEO and GEO (Generative Engine Optimization)?',
        answer:
          'Traditional SEO focuses on ranking your web pages in Google’s list of links. GEO (Generative Engine Optimization) structures your website’s data, schemas, and factual content so AI engines like ChatGPT, Perplexity, and Google AI Overviews directly cite and recommend your business when users ask conversational questions.',
      },
      {
        question: 'Does GEO replace Google SEO, or do they work together?',
        answer:
          'They work hand-in-hand. Strong technical Google SEO (fast load times, clean HTML, authority backlinks) helps AI retrieval systems find your site, while GEO (Schema.org entity graphs, /llms.txt, structured Q&A hubs) ensures the AI model extracts and quotes your brand accurately.',
      },
      {
        question: 'Can you add GEO optimization to our existing WordPress or custom website?',
        answer:
          'Yes! We can deploy our complete GEO package—including custom JSON-LD entity graphs, /llms.txt files, robots.txt AI crawler rules, and citation-optimized FAQ hubs—onto your current WordPress, Shopify, Webflow, or custom website.',
      },
      {
        question: 'How can we test our current website’s GEO readiness right now?',
        answer:
          'You can use our free 25-point Xwebaudit scanner right here on our platform to test your live domain in 60 seconds for Schema.org entities, /llms.txt presence, and AI search visibility.',
      },
    ],
  },
  {
    key: 'automation',
    slug: 'automation-pipeline',
    path: '/ai-business-automation',
    index: '06',
    badge: '24/7 CONCIERGE, WHATSAPP & CRM AUTOMATION',
    shortTitle: 'AI Business Automation',
    title: 'Autonomous Lead Qualification, WhatsApp Routing & CRM Automation',
    highlightText: 'Always-On Sales Engine',
    subtitle:
      'Capture, qualify, and route website visitors into WhatsApp, booked calendar appointments, and your CRM in under 60 seconds—24/7.',
    heroDescription:
      '78% of B2B and high-ticket buyers purchase from the first company that responds thoughtfully to their inquiry. If your website relies on a passive "Contact Us" form that waits until Monday morning for a manual reply, you are leaking revenue. We engineer autonomous 24/7 AI Sales Concierges, instant WhatsApp dispatch, automated PDF proposal generation, and two-way CRM synchronization.',
    whatsappPrefill:
      "Hello Xweba! I'd like to automate our website lead qualification, WhatsApp routing, and CRM pipeline.",
    metrics: [
      { value: '<60s', label: 'Lead Response Latency' },
      { value: '24/7', label: 'Autonomous Qualification' },
      { value: '-78%', label: 'Manual Admin Friction' },
    ],
    specCardTitle: 'AUTONOMOUS PIPELINE SPECIFICATION',
    specCardSubtitle: 'Zero-Leakage Visitor-to-Revenue Engine',
    specPillars: [
      {
        title: 'Custom-Trained 24/7 AI Sales Concierge',
        detail:
          'Grounded strictly on your services, pricing, and FAQs to answer buyer questions and qualify intent day or night.',
      },
      {
        title: 'Instant WhatsApp & Executive Email Dispatch',
        detail:
          'Routes qualified lead briefs immediately to WhatsApp (+254 725 724 175) and your sales desk with full conversation context.',
      },
      {
        title: 'Branded PDF Proposal & Confirmation Engine',
        detail:
          'Automatically generates and emails a branded corporate PDF & HTML summary to the client within seconds of submission.',
      },
      {
        title: 'Built-In Lead CRM & External Webhook Sync',
        detail:
          'Manage leads, statuses, notes, and bulk email campaigns in your dedicated CRM or sync with HubSpot, Salesforce & Zoho.',
      },
    ],
    modulesHeading: '6 Core Modules of Our AI Business Automation Suite',
    modulesSubheading:
      'We connect your website frontend directly to your sales operations so no high-intent prospect ever slips through the cracks:',
    modules: [
      {
        number: '01',
        title: '24/7 Custom AI Sales & Support Concierge Widget',
        description:
          'An intelligent, on-brand conversational assistant embedded on your website that greets visitors, answers technical questions, and guides them to book.',
        deliverables: [
          'Custom system prompt grounded on your exact services, packages, and policies',
          'Guardrails preventing off-topic answers or competitor mentions',
          'Seamless handoff to live WhatsApp chat or strategy call booking',
        ],
      },
      {
        number: '02',
        title: 'Interactive Quote, Audit & Lead Scoring Flows',
        description:
          'Replace boring contact forms with interactive multi-step qualification flows that capture scope, timeline, budget, and contact details.',
        deliverables: [
          'Custom interactive quote & package selector modals (USD / KES support)',
          'Automated lead scoring (0–100) based on budget, urgency, and fit',
          'Smart input validation and phone/email normalization',
        ],
      },
      {
        number: '03',
        title: 'Sub-60-Second WhatsApp & Multi-Channel Alerts',
        description:
          'Meet buyers on the channel they check 50 times a day. The moment a prospect submits an inquiry, both your team and the client are connected.',
        deliverables: [
          'Instant pre-formatted WhatsApp handoff with project reference ID',
          'Dual-port Hostinger SMTP email dispatch to your sales team',
          'One-click WhatsApp & email reply actions for rapid sales closing',
        ],
      },
      {
        number: '04',
        title: 'Automated Branded PDF & HTML Report Generation',
        description:
          'Impress prospects immediately by sending an automatic, custom-branded PDF confirmation or diagnostic report to their inbox upon inquiry.',
        deliverables: [
          'Server-side branded PDF generator with your company logo & colors',
          'Instant client confirmation email with attached PDF & HTML summary',
          'Permanent downloadable report URLs archived in your CRM',
        ],
      },
      {
        number: '05',
        title: 'Full-Featured Lead CRM & Pipeline Management',
        description:
          'Track every inquiry from first click to closed deal in a clean, centralized CRM dashboard—or sync two-way with your existing enterprise CRM.',
        deliverables: [
          'Lead pipeline stages (New, Contacted, Qualified, Proposal Sent, Won)',
          'Activity timeline, team notes, CSV export, and delivery telemetry logs',
          'Integration support for HubSpot, Salesforce, Pipedrive, Zoho & Notion',
        ],
      },
      {
        number: '06',
        title: 'Automated Newsletter & Bulk Email Campaign Studio',
        description:
          'Nurture every lead and subscriber in your database with branded HTML email broadcasts and automated blog-to-newsletter dispatches.',
        deliverables: [
          '1-click Digital Pulse blog article to subscriber newsletter dispatch',
          'Audience segmentation by lead status, inquiry type, or custom lists',
          'Personalization merge tags ({{name}}, {{company}}, {{domain}}) & live HTML preview',
        ],
      },
    ],
    roadmapHeading: '4-Phase Automation Deployment Protocol',
    roadmapSubheading:
      'How we transform your website into an autonomous 24/7 client acquisition system:',
    roadmap: [
      {
        phase: 'PHASE 01',
        timeframe: 'Days 1 – 2',
        title: 'Sales Workflow & Knowledge Base Mapping',
        description:
          'Documenting your qualification criteria, pricing tiers, FAQs, and routing destinations.',
        outcome: 'Approved Automation & AI Concierge Playbook',
      },
      {
        phase: 'PHASE 02',
        timeframe: 'Days 3 – 5',
        title: 'AI Concierge & Interactive Funnel Engineering',
        description:
          'Training your AI assistant and building interactive booking/quote flows with PDF generation.',
        outcome: 'Staging AI Concierge & Qualification Modals',
      },
      {
        phase: 'PHASE 03',
        timeframe: 'Days 6 – 8',
        title: 'WhatsApp, SMTP & CRM Pipeline Integration',
        description:
          'Connecting Hostinger SMTP, WhatsApp deep links, and CRM lead synchronization.',
        outcome: 'Verified <60s End-to-End Lead Delivery',
      },
      {
        phase: 'PHASE 04',
        timeframe: 'Days 9 – 10',
        title: 'Live Testing, Team Training & Launch',
        description:
          'Running live end-to-end test submissions and training your sales team on the CRM & bulk email studio.',
        outcome: '24/7 Autonomous Revenue Pipeline Live',
      },
    ],
    comparisonHeading: 'Static Contact Forms vs. Xweba AI Business Automation',
    comparisonSubheading:
      'See why automated qualification and sub-60-second response times multiply conversion rates:',
    comparisons: [
      {
        dimension: 'After-Hours & Weekend Leads',
        legacyApproach: 'Visitor fills a static form on Saturday and hears nothing until Tuesday',
        xwebaApproach: '24/7 AI Concierge answers questions, qualifies scope, and dispatches a branded PDF in <60s',
      },
      {
        dimension: 'Lead Context for Sales Team',
        legacyApproach: 'Vague "How much do you charge?" emails with no phone or budget info',
        xwebaApproach: 'Enriched CRM lead profile with package tier, budget, phone, email, and audit score',
      },
      {
        dimension: 'Follow-Up Channel',
        legacyApproach: 'Plain-text emails that get buried in promotions tabs',
        xwebaApproach: 'Simultaneous WhatsApp routing + branded HTML/PDF email confirmation',
      },
      {
        dimension: 'Database Nurturing',
        legacyApproach: 'Past inquiries sit forgotten in an email inbox folder',
        xwebaApproach: 'Centralized CRM with 1-click newsletter and bulk campaign nurturing',
      },
    ],
    caseStudySpotlight: {
      client: 'Xweba Autonomous Growth Pipeline',
      role: '24/7 AI Concierge, Xwebaudit PDF Engine & Hostinger CRM',
      summary:
        'Deployed our 7-step visitor-to-revenue pipeline combining live domain diagnostics, instant PDF report generation, dual-port Hostinger SMTP delivery, and WhatsApp routing.',
      results: [
        'Sub-60-second automated confirmation with attached branded PDF report',
        '100% lead capture into unified CRM with zero manual data entry',
        '3.2x increase in booked strategy consultations from evening & weekend traffic',
      ],
    },
    faqs: [
      {
        question: 'Will the AI Concierge ever invent false prices or make unauthorized promises?',
        answer:
          'No. We engineer strict system instructions and ground the AI Concierge exclusively on your approved service tiers, pricing, and FAQs. For custom or complex enterprise requests, it collects the prospect’s details and routes them directly to your human team on WhatsApp or calendar.',
      },
      {
        question: 'Can this automation work with our existing website and email provider?',
        answer:
          'Yes. We can integrate our AI Concierge, interactive booking modals, WhatsApp routing, and SMTP/CRM pipeline into both Pro WordPress websites and custom web platforms.',
      },
      {
        question: 'How does the automated PDF report generation work?',
        answer:
          'Whenever a prospect requests a quote, books a session, or runs an audit, our backend server dynamically compiles a clean, branded corporate PDF document with their reference ID and project details, saves it to your CRM, and emails it to both you and the client.',
      },
      {
        question: 'Do we get access to the CRM and Newsletter studio?',
        answer:
          'Yes! You get full administrative access to manage leads, update pipeline statuses, download CSV/PDF reports, publish Digital Pulse blog posts, and send branded newsletters to your subscribers.',
      },
    ],
  },
  {
    key: 'hosting',
    slug: 'hostinger-cloud',
    path: '/services/hostinger-cloud',
    index: '07',
    badge: 'OFFICIAL HOSTINGER CLOUD INFRASTRUCTURE PARTNER',
    shortTitle: 'Hostinger Cloud & Domains',
    title: 'Managed Hostinger Cloud Deployment, NVMe Speed & Domain Engineering',
    highlightText: 'Hostinger Cloud Partner',
    subtitle:
      'Enterprise LiteSpeed cloud hosting, custom domain provisioning, automated daily backups, and dedicated business email infrastructure.',
    heroDescription:
      'Even the best-designed website will fail if hosted on slow, overcrowded shared servers. As an official Hostinger Infrastructure Partner, Xweba provisions, hardens, and manages high-speed NVMe Cloud and LiteSpeed environments optimized for both East African and global traffic—complete with free SSL, daily snapshots, DDoS protection, and corporate SMTP email.',
    whatsappPrefill:
      "Hello Xweba! I'd like help with Managed Hostinger Cloud Hosting, domain setup, or server migration.",
    metrics: [
      { value: '99.9%', label: 'Guaranteed Uptime SLA' },
      { value: '<120ms', label: 'LiteSpeed TTFB Response' },
      { value: 'Daily', label: 'Automated Cloud Backups' },
    ],
    specCardTitle: 'HOSTINGER CLOUD STACK SPECIFICATION',
    specCardSubtitle: 'Official Partner Infrastructure (Code: 1JOHN0542)',
    specPillars: [
      {
        title: 'NVMe SSD & LiteSpeed Enterprise Web Server',
        detail:
          'Up to 4x faster page loads than standard Apache/Nginx hosting with built-in server-level LSCache for Pro WordPress & web apps.',
      },
      {
        title: 'Custom Domain DNS, SSL/TLS 1.3 & HSTS Security',
        detail:
          'Full management of .com, .co.ke, .org, and .ai domains with Cloudflare CDN, DNSSEC, and enterprise DDoS mitigation.',
      },
      {
        title: 'Corporate Business Email & Deliverability Hardening',
        detail:
          'Dedicated company email accounts (info@yourdomain.com) configured with SPF, DKIM, DMARC, and MTA-STS for 100% inbox placement.',
      },
      {
        title: 'Zero-Downtime Migration & Daily Snapshot Recovery',
        detail:
          'Seamless migration from legacy hosts plus automated daily cloud backups with 1-click restore protection.',
      },
    ],
    modulesHeading: '6 Infrastructure Modules Included in Our Managed Cloud Service',
    modulesSubheading:
      'We handle every server, DNS, SSL, and email configuration detail so your team never has to touch a control panel:',
    modules: [
      {
        number: '01',
        title: 'Hostinger Cloud Server Provisioning & Sizing',
        description:
          'We help you select and provision the ideal Hostinger tier (Business Web, Cloud Startup, or Cloud Professional) with exclusive partner discounts.',
        deliverables: [
          'Dedicated CPU/RAM cloud resource allocation with isolated containers',
          'Global datacenter selection (Europe, USA, Asia) with African CDN edge caching',
          'Official Hostinger Partner referral savings applied directly to your account',
        ],
      },
      {
        number: '02',
        title: 'Domain Registration, DNS Zone & Security Headers',
        description:
          'We configure your domain architecture for maximum security, instant DNS resolution, and 100% technical SEO compliance.',
        deliverables: [
          'Registration or transfer of .com, .co.ke, .org, .africa, or .ai domains',
          'Auto-renewing SSL/TLS certificates with Strict-Transport-Security (HSTS)',
          'Content-Security-Policy (CSP), X-Frame, and Referrer-Policy header hardening',
        ],
      },
      {
        number: '03',
        title: 'LiteSpeed Cache, Object Caching & Core Web Vitals Tuning',
        description:
          'We tune the server stack specifically for Pro WordPress and modern web applications to achieve sub-second load times.',
        deliverables: [
          'LiteSpeed Enterprise Server + LSCache & Redis object caching activation',
          'HTTP/3 (QUIC) protocol and Brotli/Gzip compression configuration',
          'Database query optimization and automated asset CDN distribution',
        ],
      },
      {
        number: '04',
        title: 'Corporate Email Setup & SPF / DKIM / DMARC Authentication',
        description:
          'Ensure your business emails and automated CRM notifications never land in spam folders.',
        deliverables: [
          'Creation of professional mailboxes (e.g., info@, sales@, support@yourdomain.com)',
          'Full cryptographic email authentication: SPF, DKIM, DMARC & /.well-known/mta-sts.txt',
          'Dual-port SMTP (465 SSL / 587 STARTTLS) integration with your website forms',
        ],
      },
      {
        number: '05',
        title: 'Zero-Downtime Website & Email Migration',
        description:
          'Stuck on a slow, expensive, or unreliable host? Our engineers migrate your entire website, database, and emails with zero downtime.',
        deliverables: [
          'Full file system, MySQL/PostgreSQL database, and mailbox replication',
          'Staging verification before switching live DNS nameservers',
          'Zero lost emails or broken URLs during transition',
        ],
      },
      {
        number: '06',
        title: '24/7 Uptime Monitoring, Malware Firewall & Daily Backups',
        description:
          'Sleep soundly knowing your digital infrastructure is protected by automated daily snapshots and proactive security rules.',
        deliverables: [
          'Automated daily & weekly off-site cloud backups with 1-click rollback',
          'Web Application Firewall (WAF), brute-force protection & malware scanning',
          'Continuous uptime monitoring and SSL expiration safeguards',
        ],
      },
    ],
    roadmapHeading: '4-Step Cloud Deployment & Migration Process',
    roadmapSubheading:
      'From legacy hosting to high-speed Hostinger Cloud in 48 to 72 hours:',
    roadmap: [
      {
        phase: 'PHASE 01',
        timeframe: 'Day 1',
        title: 'Infrastructure Audit & Tier Selection',
        description:
          'Reviewing your current traffic, storage, email accounts, and selecting the optimal Hostinger Cloud tier.',
        outcome: 'Provisioned Hostinger Cloud Environment',
      },
      {
        phase: 'PHASE 02',
        timeframe: 'Day 2',
        title: 'Staging Migration & LiteSpeed Optimization',
        description:
          'Cloning your site or deploying your new build to staging, configuring LSCache, and testing speed.',
        outcome: 'Verified Sub-Second Staging Deployment',
      },
      {
        phase: 'PHASE 03',
        timeframe: 'Day 3',
        title: 'DNS Cutover, SSL & Email Authentication',
        description:
          'Pointing nameservers with zero downtime, activating SSL/HSTS, and verifying SPF/DKIM/DMARC records.',
        outcome: '100% Secure Live Production Domain & Mailboxes',
      },
      {
        phase: 'PHASE 04',
        timeframe: 'Ongoing',
        title: 'Automated Backups & WaaS Care',
        description:
          'Enabling daily snapshots, security patches, and continuous performance monitoring.',
        outcome: '99.9% Uptime SLA & Peace of Mind',
      },
    ],
    comparisonHeading: 'Legacy Local Shared Hosting vs. Xweba Managed Hostinger Cloud',
    comparisonSubheading:
      'See how enterprise NVMe cloud infrastructure impacts speed, SEO, and reliability:',
    comparisons: [
      {
        dimension: 'Server Architecture',
        legacyApproach: 'Overcrowded HDD/SATA cPanel shared servers with frequent timeouts',
        xwebaApproach: 'Isolated NVMe SSD Cloud containers with LiteSpeed Enterprise & HTTP/3',
      },
      {
        dimension: 'Email Deliverability',
        legacyApproach: 'Shared IP addresses blacklisted by Gmail and Outlook spam filters',
        xwebaApproach: 'Authenticated Hostinger SMTP with strict SPF, DKIM, DMARC & MTA-STS',
      },
      {
        dimension: 'Backup & Recovery',
        legacyApproach: 'Manual backups or extra fees when something breaks',
        xwebaApproach: 'Automated daily cloud snapshots with instant 1-click restoration',
      },
      {
        dimension: 'Account Ownership',
        legacyApproach: 'Held hostage by an agency that refuses to release your domain or cPanel',
        xwebaApproach: '100% transparent client ownership or all-inclusive WaaS management',
      },
    ],
    caseStudySpotlight: {
      client: 'Xweba Partner Cloud Deployments',
      url: 'https://www.hostinger.com?REFERRALCODE=1JOHN0542',
      role: 'Official Hostinger Cloud Provisioning & High-Speed Edge Delivery',
      summary:
        'All Xweba client websites—including Pro WordPress and custom AI platforms—are deployed on optimized Hostinger Cloud stacks, delivering sub-second load speeds and 99.9% uptime across East Africa, Europe, and North America.',
      results: [
        'Sub-800ms average mobile page load speed across production sites',
        '99.9% verified uptime with automated daily snapshot backups',
        'Exclusive Partner Referral savings for all Xweba clients',
      ],
    },
    faqs: [
      {
        question: 'Do we own our Hostinger hosting account and domain name?',
        answer:
          'Yes! You can either register directly through our Official Hostinger Partner link (claiming our partner discount while granting our team collaborator access to set everything up for you) or have us manage it turnkey under our Website-as-a-Service (WaaS) plan.',
      },
      {
        question: 'Can you migrate our existing website and business emails from another host without losing emails?',
        answer:
          'Yes. We perform a synchronized staging migration of your website files, databases, and IMAP mailboxes before switching DNS, ensuring zero website downtime and zero lost emails.',
      },
      {
        question: 'Does Hostinger Cloud work well for Kenyan (.co.ke) and international domains?',
        answer:
          'Absolutely. We configure both international (.com, .org, .ai) and local (.co.ke, .africa) domains with global CDN edge nodes so visitors in Nairobi, London, New York, or Dubai experience instant load times.',
      },
      {
        question: 'Is hosting included in Xweba’s monthly Website-as-a-Service (WaaS) packages?',
        answer:
          'Yes! All of our monthly WaaS packages include managed high-speed cloud hosting, SSL security, daily backups, and ongoing technical maintenance.',
      },
    ],
  },
];

export const SERVICE_DETAILS_BY_KEY: Record<ServiceKey, ServiceDetailData> =
  SERVICE_DETAILS_LIST.reduce((acc, item) => {
    acc[item.key] = item;
    return acc;
  }, {} as Record<ServiceKey, ServiceDetailData>);

export function getServiceDetailByKeyOrSlug(query: string): ServiceDetailData | undefined {
  const clean = query
    .toLowerCase()
    .replace(/^#?\/?/, '')
    .replace(/^services\//, '')
    .replace(/\/+$/, '');

  return SERVICE_DETAILS_LIST.find(
    (s) =>
      s.key === clean ||
      s.slug === clean ||
      s.path.toLowerCase().replace(/^\//, '') === clean ||
      (clean === 'ai-websites' && s.key === 'websites') ||
      (clean === 'ai-search-visibility' && s.key === 'search') ||
      (clean === 'ai-business-automation' && s.key === 'automation') ||
      (clean === 'pro-wordpress-ai-websites' && s.key === 'websites') ||
      (clean === 'geo-optimization' && s.key === 'search') ||
      (clean === 'automation-pipeline' && s.key === 'automation')
  );
}

